import { useNavigate } from 'react-router-dom'
import { ManageTable, type ManageField } from '@/components/ManageTable'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { InputNumber } from '@/components/ui/input-number'
import { CodeEditor } from '@/components/CodeEditor'
import { Button } from '@/components/ui/button'
import { Eye } from 'lucide-react'
import { useList } from '@/hooks/useList'
import { usePersistentState } from '@/hooks/usePersistentState'

interface Plat {
  id: string
  name: string
}

interface User {
  id: string
  username: string
}

export default function Task() {
  const navigate = useNavigate()
  const [plats] = useList<Plat>('plat')
  const [users] = useList<User>('user')
  const [filters, setFilter, clearFilters] = usePersistentState<Record<string, any>>(
    'admin:task:filters',
    {},
  )

  const handleResetFilters = () => {
    clearFilters()
  }

  const fields: ManageField[] = [
    {
      desc: '任务名称',
      field: 'name',
    },
    {
      desc: '余额',
      field: 'balance',
      Comp: <InputNumber />,
      render: (val) => (
        <span className="font-medium text-emerald-600 dark:text-emerald-400 text-right block">
          {val?.toFixed(2) || '0.00'}
        </span>
      ),
    },
    {
      desc: '限额',
      field: 'max',
      Comp: <InputNumber />,
      render: (val) => val ? (
        <span className="font-medium text-orange-600 dark:text-orange-400 text-right block">
          {val}
        </span>
      ) : <span className="text-muted-foreground text-right block">-</span>,
    },
    {
      desc: '运行状态',
      field: 'status',
      render: (val) => {
        const statusMap: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string; icon?: string }> = {
          running: { variant: 'default', label: '运行中', icon: '🔄' },
          runing: { variant: 'default', label: '运行中', icon: '🔄' },
          stop: { variant: 'secondary', label: '已停止', icon: '⏸️' }
        }
        const info = statusMap[val] || { variant: 'outline' as const, label: val, icon: undefined }
        return (
          <Badge variant={info.variant} className="gap-1">
            {info.icon && <span>{info.icon}</span>}
            {info.label}
          </Badge>
        )
      },
    },
    {
      desc: '运行模式',
      field: 'mode',
      render: (val) => {
        if (!val) return <span className="text-muted-foreground">-</span>
        const modeMap: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string; icon?: string; color?: string }> = {
          run: { variant: 'default', label: 'RUN', icon: '▶', color: 'bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700' },
          rd: { variant: 'secondary', label: 'R&D', icon: '🔬', color: 'bg-purple-500 hover:bg-purple-600 dark:bg-purple-600 dark:hover:bg-purple-700' }
        }
        const info = modeMap[val.toLowerCase()] || { variant: 'outline' as const, label: val.toUpperCase(), icon: undefined, color: undefined }
        return (
          <Badge
            variant={info.variant}
            className={`font-mono gap-1 font-semibold ${info.color || ''}`}
          >
            {info.icon && <span className="text-xs">{info.icon}</span>}
            {info.label}
          </Badge>
        )
      },
    },
    {
      desc: '窗口状态',
      field: 'window_status',
      render: (val) => {
        if (!val) return <span className="text-muted-foreground">-</span>
        const statusMap: Record<string, { color: string; label: string }> = {
          open: { color: 'text-green-600 dark:text-green-400', label: '✓ 开启' },
          close: { color: 'text-gray-500', label: '○ 关闭' },
          pending: { color: 'text-yellow-600 dark:text-yellow-400', label: '◐ 待开' }
        }
        const info = statusMap[val] || { color: 'text-gray-500', label: val }
        return <span className={`font-medium ${info.color}`}>{info.label}</span>
      },
    },
    {
      desc: 'IP时间',
      field: 'ip_cache_create_time',
      render: (val) => {
        if (!val) return <span className="text-muted-foreground">-</span>
        return <span className="text-sm text-muted-foreground">{val}</span>
      },
    },
    {
      desc: '已运行',
      field: 'use_count',
      Comp: <InputNumber />,
    },
    {
      desc: '本次运行',
      field: 'current_count',
      Comp: <InputNumber />,
    },
    {
      desc: '账户',
      field: 'account',
      NoShowInTable: true,
    },
    {
      desc: '策略',
      field: 'strategy',
      NoShowInTable: true,
    },
    {
      desc: '平台',
      field: 'plat',
      NoShowInTable: true,
    },
    {
      desc: '节点',
      field: 'node_ip',
      NoShowInTable: true,
    },
    {
      desc: '策略hook',
      field: 'strategy_hook',
      NoShowInTable: true,
    },
    {
      desc: '提现次数',
      field: 'wd_count',
      Comp: <InputNumber />,
      NoShowInTable: true,
    },
    {
      desc: '随机天数',
      field: 'wd_day_count',
      Comp: <InputNumber />,
    },
    {
      desc: '自动',
      field: 'auto',
      valuePropName: 'checked',
      Comp: <Switch />,
      render: (val) => val ? '是' : '否',
      NoShowInTable: true,
    },
    {
      desc: '更新时间',
      field: 'update_time',
      NoShowInTable: true,
    },
    {
      desc: '启动Hook',
      field: 'start_hook',
      Comp: <CodeEditor language="python" />,
      NoShowInTable: true,
    },
    {
      desc: '结束Hook',
      field: 'end_hook',
      Comp: <CodeEditor language="python" />,
      NoShowInTable: true,
    },
  ]

  const filterUI = (
    <div className="flex items-center gap-4 flex-wrap">
      <div className="flex items-center gap-2">
        <span className="text-sm">平台:</span>
        <Select
          value={filters.plat || 'all'}
          onValueChange={(val) => {
            if (val === 'all') {
              const newFilters = { ...filters }
              delete newFilters.plat
              setFilter(newFilters)
            } else {
              setFilter({ ...filters, plat: val })
            }
          }}
        >
          <SelectTrigger className="w-[120px]">
            <SelectValue placeholder="全部" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">全部</SelectItem>
            {plats.map((plat) => (
              <SelectItem key={plat.id} value={plat.name}>
                {plat.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-sm">用户:</span>
        <Select
          value={filters.user || 'all'}
          onValueChange={(val) => {
            if (val === 'all') {
              const newFilters = { ...filters }
              delete newFilters.user
              setFilter(newFilters)
            } else {
              setFilter({ ...filters, user: val })
            }
          }}
        >
          <SelectTrigger className="w-[120px]">
            <SelectValue placeholder="全部" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">全部</SelectItem>
            {users.map((user) => (
              <SelectItem key={user.id} value={user.username}>
                {user.username}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-sm">模式:</span>
        <Select
          value={filters.mode || 'all'}
          onValueChange={(val) => {
            if (val === 'all') {
              const newFilters = { ...filters }
              delete newFilters.mode
              setFilter(newFilters)
            } else {
              setFilter({ ...filters, mode: val })
            }
          }}
        >
          <SelectTrigger className="w-[120px]">
            <SelectValue placeholder="全部" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">全部</SelectItem>
            <SelectItem value="run">run</SelectItem>
            <SelectItem value="rd">rd</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-sm">状态:</span>
        <Select
          value={filters.status || 'all'}
          onValueChange={(val) => {
            if (val === 'all') {
              const newFilters = { ...filters }
              delete newFilters.status
              setFilter(newFilters)
            } else {
              setFilter({ ...filters, status: val })
            }
          }}
        >
          <SelectTrigger className="w-[120px]">
            <SelectValue placeholder="全部" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">全部</SelectItem>
            <SelectItem value="runing">运行中</SelectItem>
            <SelectItem value="stop">已停止</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  )

  return (
    <div className="h-full p-6 overflow-auto">
      <h1 className="text-2xl font-bold mb-6">任务管理</h1>
      <ManageTable
        fields={fields}
        filter={filterUI}
        filters={filters}
        collect="task"
        modalWidth="70%"
        onResetFilters={handleResetFilters}
        customActions={(row) => (
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate(`/admin/task/${row.id}`)}
          >
            <Eye className="h-4 w-4 mr-1" />
            详情
          </Button>
        )}
      />
    </div>
  )
}
